/**
 * Credit Event Summary overview — same Roomgrid dashboard language as
 * Statistics (KPI cards with deltas + stacked monthly chart + panels),
 * computed CLIENT-side from the monthly summary rows (costCenter × app ×
 * channel × year/month), which are small enough to aggregate in one fetch.
 */

import React from "react";
import { useDataProvider, useNotify, useTranslate } from "react-admin";
import { Box, Skeleton, Tooltip, Typography, useTheme } from "@mui/material";
import { extractErrorMessage } from "@/utils/extractErrorMessage";
import { ChannelChip } from "@/components/common";
import { KpiCard, PanelBox } from "@/features/statistics/components/StatisticsDashboard";
import type { CreditEventLogRecord } from "@/features/credit-event-logs/types/creditEventLog";
import type { CreditEventSummaryRecord } from "../types/creditEventSummary";

/**
 * Roll raw credit event logs up into the same monthly summary shape
 * (costCenter × app × channel × year/month) the dashboard consumes. Recent
 * activity lives in the raw logs (the summary table only holds events already
 * archived by the daily rollup job), so this is what makes current usage show.
 */
const aggregateLogs = (
  logs: CreditEventLogRecord[],
): CreditEventSummaryRecord[] => {
  const map = new Map<string, CreditEventSummaryRecord>();
  logs.forEach((l) => {
    const d = new Date(l.createdAt);
    if (Number.isNaN(d.getTime())) return;
    const year = d.getUTCFullYear();
    const month = d.getUTCMonth() + 1;
    const key = `${l.costCenterName}|${l.applicationName}|${l.channel}|${year}-${month}`;
    let rec = map.get(key);
    if (!rec) {
      rec = {
        id: key,
        costCenterName: l.costCenterName,
        applicationName: l.applicationName,
        channel: l.channel,
        year,
        month,
        totalMessages: 0,
        totalCreditsConsumed: 0,
        createdAt: l.createdAt,
      };
      map.set(key, rec);
    }
    rec.totalMessages += 1;
    rec.totalCreditsConsumed += Number(l.creditsConsumed) || 0;
  });
  return Array.from(map.values());
};

const i18n = "resources.credit-consumption/credit-event-summary.overview";

type MonthsKey = "current" | "last" | 3 | 6 | 12;

const CHANNEL_ORDER = ["email", "whatsapp", "sms", "telegram", "webpush", "apppush", "other"];

const channelKey = (raw: string): string => {
  const k = (raw ?? "").toLowerCase().replace(/[\s_-]/g, "");
  return CHANNEL_ORDER.includes(k) ? k : k === "push" ? "apppush" : CHANNEL_ORDER.includes(k) ? k : ["email","sms","whatsapp","telegram","webpush","apppush"].includes(k) ? k : "other";
};

const monthLabel = (ym: string): string => ym.slice(2).replace("-", "/"); // 26/07

/** Last n year-months, oldest first, ending at the current month. */
const lastMonths = (n: number, endOffset = 0): string[] => {
  const out: string[] = [];
  const now = new Date();
  for (let i = n - 1 + endOffset; i >= endOffset; i--) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`);
  }
  return out;
};

const ymOf = (r: CreditEventSummaryRecord): string =>
  `${r.year}-${String(r.month).padStart(2, "0")}`;

const fmtCredits = (v: number): string =>
  v.toLocaleString(undefined, { maximumFractionDigits: 2 });

const useChannelColors = (): Record<string, string> => {
  const theme = useTheme();
  const rg = theme.custom.rg;
  return {
    email: rg.accent,
    whatsapp: "#25D366",
    sms: rg.violet.fg,
    telegram: "#26A5E4",
    webpush: rg.warn.fg,
    apppush: rg.neutral.fg,
    other: rg.text4,
  };
};

const cellSx = { fontSize: "12.5px", py: "7px", px: "10px" } as const;
const headSx = {
  fontSize: "10.5px",
  fontWeight: 600,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  py: "6px",
  px: "10px",
  textAlign: "left" as const,
};

export const CreditSummaryOverview: React.FC = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const theme = useTheme();
  const rg = theme.custom.rg;
  const colors = useChannelColors();

  const [months, setMonths] = React.useState<MonthsKey>("current");
  const [rows, setRows] = React.useState<CreditEventSummaryRecord[] | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    const opts = {
      pagination: { page: 1, perPage: 10000 },
      sort: { field: "createdAt", order: "DESC" as const },
      filter: {},
    };
    Promise.all([
      // Live: recent per-event consumption, aggregated client-side.
      dataProvider.getList<CreditEventLogRecord>(
        "credit-consumption/credit-event-logs",
        opts,
      ),
      // Historical: months already archived out of the raw logs (may be empty).
      dataProvider
        .getList<CreditEventSummaryRecord>(
          "credit-consumption/credit-event-summary",
          opts,
        )
        .catch(() => ({ data: [] as CreditEventSummaryRecord[] })),
    ])
      .then(([logsRes, summaryRes]) => {
        if (!cancelled)
          setRows([...aggregateLogs(logsRes.data), ...summaryRes.data]);
      })
      .catch((err: unknown) => {
        if (!cancelled)
          notify(extractErrorMessage(err, "ra.notification.http_error", translate), {
            type: "error",
          });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const t = (key: string, fallback: string) => translate(`${i18n}.${key}`, { _: fallback });

  // "current" = this month (1st → now) vs last month; "last" = last month
  // vs the month before; numeric keys = rolling windows of n months.
  const curMonths =
    months === "current" ? lastMonths(1) : months === "last" ? lastMonths(1, 1) : lastMonths(months);
  const prevMonths =
    months === "current" ? lastMonths(1, 1) : months === "last" ? lastMonths(1, 2) : lastMonths(months, months);
  const cur = (rows ?? []).filter((r) => curMonths.includes(ymOf(r)));
  const prev = (rows ?? []).filter((r) => prevMonths.includes(ymOf(r)));

  const sum = (list: CreditEventSummaryRecord[], f: (r: CreditEventSummaryRecord) => number) =>
    list.reduce((acc, r) => acc + f(r), 0);

  const credits = sum(cur, (r) => r.totalCreditsConsumed);
  const prevCredits = sum(prev, (r) => r.totalCreditsConsumed);
  const messages = sum(cur, (r) => r.totalMessages);
  const prevMessages = sum(prev, (r) => r.totalMessages);
  const activeCenters = new Set(cur.map((r) => r.costCenterName)).size;
  const prevActiveCenters = new Set(prev.map((r) => r.costCenterName)).size;
  const avgPerMsg = messages > 0 ? credits / messages : null;

  const deltaOf = (
    now: number,
    before: number,
    higherIsBetter: boolean,
    fmt: (v: number) => string = (v) => v.toLocaleString(),
  ): { text: string; tone: "good" | "bad" | "flat" } => {
    const diff = now - before;
    if (diff === 0) return { text: t("deltaFlat", "▬ no change"), tone: "flat" };
    const arrow = diff > 0 ? "▲" : "▼";
    const tone = diff > 0 === higherIsBetter ? "good" : "bad";
    const pct = before > 0 ? ` (${Math.abs(Math.round((100 * diff) / before))}%)` : "";
    return {
      text: `${arrow} ${fmt(Math.abs(diff))}${pct} ${t("deltaVsPrev", "vs. previous period")}`,
      tone,
    };
  };

  // Monthly stacked series (credits by channel).
  const byMonth = curMonths.map((ym) => {
    const monthRows = cur.filter((r) => ymOf(r) === ym);
    const byChannel: Record<string, number> = {};
    monthRows.forEach((r) => {
      const k = channelKey(r.channel);
      byChannel[k] = (byChannel[k] ?? 0) + r.totalCreditsConsumed;
    });
    return {
      ym,
      total: monthRows.reduce((a, r) => a + r.totalCreditsConsumed, 0),
      messages: monthRows.reduce((a, r) => a + r.totalMessages, 0),
      byChannel,
    };
  });
  const maxMonth = Math.max(1, ...byMonth.map((m) => m.total));

  const byChannel = CHANNEL_ORDER.map((c) => {
    const chRows = cur.filter((r) => channelKey(r.channel) === c);
    return {
      channel: c,
      credits: sum(chRows, (r) => r.totalCreditsConsumed),
      messages: sum(chRows, (r) => r.totalMessages),
    };
  })
    .filter((r) => r.messages > 0 || r.credits > 0)
    .sort((a, b) => b.credits - a.credits);

  const centerMap = new Map<string, { credits: number; messages: number }>();
  cur.forEach((r) => {
    const e = centerMap.get(r.costCenterName) ?? { credits: 0, messages: 0 };
    e.credits += r.totalCreditsConsumed;
    e.messages += r.totalMessages;
    centerMap.set(r.costCenterName, e);
  });
  const topCenters = Array.from(centerMap.entries())
    .map(([name, v]) => ({ name, ...v }))
    .sort((a, b) => b.credits - a.credits)
    .slice(0, 8);

  const loading = rows == null;
  const monthButtons: Array<{ key: MonthsKey; label: string }> = [
    { key: "current", label: t("monthsCurrent", "Current month") },
    { key: "last", label: t("monthsLast", "Last month") },
    { key: 3, label: t("months3", "3 months") },
    { key: 6, label: t("months6", "6 months") },
    { key: 12, label: t("months12", "12 months") },
  ];

  return (
    <Box sx={{ mb: 3 }}>
      {/* -------- period control -------- */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
        <Typography
          sx={{
            fontSize: "10.5px",
            fontWeight: 600,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: rg.text3,
            mb: "5px",
          }}
        >
          {t("period", "Period")}
        </Typography>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "stretch",
            height: 40,
            border: `1px solid ${rg.border}`,
            borderRadius: "8px",
            overflow: "hidden",
            backgroundColor: rg.bgElev,
          }}
        >
          {monthButtons.map((m, i) => (
            <Box
              key={m.key}
              component="button"
              onClick={() => setMonths(m.key)}
              sx={{
                border: "none",
                borderRight: i < monthButtons.length - 1 ? `1px solid ${rg.border}` : "none",
                padding: "0 14px",
                fontSize: "12px",
                fontWeight: 600,
                fontFamily: "inherit",
                cursor: "pointer",
                backgroundColor: months === m.key ? rg.text : "transparent",
                color: months === m.key ? rg.bg : rg.text2,
              }}
            >
              {m.label}
            </Box>
          ))}
        </Box>
        </Box>
      </Box>

      {/* -------- KPIs -------- */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
          gap: "14px",
          mb: "20px",
        }}
      >
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={104} sx={{ borderRadius: "14px" }} />
          ))
        ) : (
          <>
            <KpiCard
              label={t("kpiCredits", "Credits consumed")}
              value={fmtCredits(credits)}
              delta={deltaOf(credits, prevCredits, false, fmtCredits)}
            />
            <KpiCard
              label={t("kpiMessages", "Messages")}
              value={messages.toLocaleString()}
              delta={deltaOf(messages, prevMessages, true)}
            />
            <KpiCard
              label={t("kpiAvgPerMsg", "Credits / message")}
              value={avgPerMsg == null ? "—" : avgPerMsg.toLocaleString(undefined, { maximumFractionDigits: 3 })}
            />
            <KpiCard
              label={t("kpiCostCenters", "Active cost centers")}
              value={activeCenters.toLocaleString()}
              delta={deltaOf(activeCenters, prevActiveCenters, true)}
            />
          </>
        )}
      </Box>

      {/* -------- monthly chart -------- */}
      <PanelBox
        title={t("chartTitle", "Credits per month and channel")}
        subtitle={t("chartSubtitle", "Stacked by channel")}
      >
        {loading ? (
          <Skeleton variant="rounded" height={150} />
        ) : (
          <>
            <Box sx={{ display: "flex", alignItems: "flex-end", gap: "8px", height: 150 }}>
              {byMonth.map((m) => {
                const channels = CHANNEL_ORDER.filter((c) => (m.byChannel[c] ?? 0) > 0);
                const tooltip = [
                  `${m.ym} — ${fmtCredits(m.total)} ${t("credits", "credits")} · ${m.messages.toLocaleString()} ${t("messages", "messages")}`,
                  ...channels.map((c) => `${c}: ${fmtCredits(m.byChannel[c])}`),
                ].join(" · ");
                return (
                  <Tooltip key={m.ym} title={tooltip}>
                    <Box
                      sx={{
                        flex: 1,
                        height: `${Math.max(3, (m.total / maxMonth) * 100)}%`,
                        display: "flex",
                        flexDirection: "column-reverse",
                        gap: "1px",
                        minWidth: 0,
                      }}
                    >
                      {channels.map((c) => (
                        <Box
                          key={c}
                          sx={{
                            flexGrow: (m.byChannel[c] ?? 0) / (m.total || 1),
                            backgroundColor: colors[c],
                            borderRadius: "2px",
                            minHeight: "2px",
                          }}
                        />
                      ))}
                      {m.total === 0 && (
                        <Box sx={{ height: 2, backgroundColor: rg.border, borderRadius: "2px" }} />
                      )}
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
            <Box sx={{ display: "flex", gap: "8px", mt: "6px" }}>
              {byMonth.map((m) => (
                <Typography
                  key={m.ym}
                  className="rg-mono"
                  sx={{ flex: 1, textAlign: "center", fontSize: "9.5px", color: rg.text3 }}
                >
                  {monthLabel(m.ym)}
                </Typography>
              ))}
            </Box>
            <Box sx={{ display: "flex", gap: "14px", flexWrap: "wrap", mt: "12px" }}>
              {CHANNEL_ORDER.filter((c) => byMonth.some((m) => (m.byChannel[c] ?? 0) > 0)).map((c) => (
                <Box
                  key={c}
                  sx={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "11px", color: rg.text2 }}
                >
                  <Box sx={{ width: 9, height: 9, borderRadius: "2px", backgroundColor: colors[c] }} />
                  {c === "webpush" ? "Web Push" : c === "apppush" ? "App Push" : c[0].toUpperCase() + c.slice(1)}
                </Box>
              ))}
            </Box>
          </>
        )}
      </PanelBox>

      <Box sx={{ height: 20 }} />

      {/* -------- panels -------- */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: "20px" }}>
        <PanelBox
          title={t("byChannelTitle", "By channel")}
          subtitle={t("byChannelSubtitle", "Credits and messages for the period")}
        >
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
            <Box component="thead">
              <Box component="tr" sx={{ "& th": { ...headSx, color: rg.text3, borderBottom: `1px solid ${rg.border}` } }}>
                <th>{t("colChannel", "Channel")}</th>
                <th style={{ textAlign: "right" }}>{t("colMessages", "Messages")}</th>
                <th style={{ textAlign: "right" }}>{t("colCredits", "Credits")}</th>
                <th style={{ textAlign: "right" }}>{t("colAvg", "Credits/msg")}</th>
              </Box>
            </Box>
            <Box component="tbody">
              {byChannel.map((row) => (
                <Box component="tr" key={row.channel} sx={{ "& td": { ...cellSx, borderBottom: `1px solid ${rg.border}` }, "&:last-child td": { borderBottom: "none" } }}>
                  <td><ChannelChip channel={row.channel} /></td>
                  <td className="rg-mono" style={{ textAlign: "right" }}>{row.messages.toLocaleString()}</td>
                  <td className="rg-mono" style={{ textAlign: "right" }}>{fmtCredits(row.credits)}</td>
                  <td className="rg-mono" style={{ textAlign: "right" }}>
                    {row.messages > 0 ? (row.credits / row.messages).toLocaleString(undefined, { maximumFractionDigits: 3 }) : "—"}
                  </td>
                </Box>
              ))}
              {!loading && byChannel.length === 0 && (
                <Box component="tr"><td colSpan={4} style={{ ...cellSx, color: rg.text3, textAlign: "center" }}>—</td></Box>
              )}
            </Box>
          </Box>
        </PanelBox>

        <PanelBox
          title={t("topCentersTitle", "Top cost centers")}
          subtitle={t("topCentersSubtitle", "By credits consumed in the period")}
        >
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
            <Box component="thead">
              <Box component="tr" sx={{ "& th": { ...headSx, color: rg.text3, borderBottom: `1px solid ${rg.border}` } }}>
                <th>{t("colCostCenter", "Cost center")}</th>
                <th style={{ textAlign: "right" }}>{t("colMessages", "Messages")}</th>
                <th style={{ textAlign: "right" }}>{t("colCredits", "Credits")}</th>
                <th>{t("colShare", "Share")}</th>
              </Box>
            </Box>
            <Box component="tbody">
              {topCenters.map((row) => (
                <Box component="tr" key={row.name} sx={{ "& td": { ...cellSx, borderBottom: `1px solid ${rg.border}` }, "&:last-child td": { borderBottom: "none" } }}>
                  <td>{row.name || "—"}</td>
                  <td className="rg-mono" style={{ textAlign: "right" }}>{row.messages.toLocaleString()}</td>
                  <td className="rg-mono" style={{ textAlign: "right" }}>{fmtCredits(row.credits)}</td>
                  <td>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <Box sx={{ flex: 1, minWidth: 60, height: 5, borderRadius: "99px", backgroundColor: rg.border, overflow: "hidden" }}>
                        <Box
                          sx={{
                            width: `${credits > 0 ? (100 * row.credits) / credits : 0}%`,
                            height: "100%",
                            borderRadius: "99px",
                            backgroundColor: rg.accent,
                          }}
                        />
                      </Box>
                      <Typography className="rg-mono" sx={{ fontSize: "12px", minWidth: 40, textAlign: "right" }}>
                        {credits > 0 ? `${Math.round((100 * row.credits) / credits)}%` : "—"}
                      </Typography>
                    </Box>
                  </td>
                </Box>
              ))}
              {!loading && topCenters.length === 0 && (
                <Box component="tr"><td colSpan={4} style={{ ...cellSx, color: rg.text3, textAlign: "center" }}>—</td></Box>
              )}
            </Box>
          </Box>
        </PanelBox>
      </Box>
    </Box>
  );
};

export default CreditSummaryOverview;
